import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Types for ID Verification
export interface IDVerificationTask {
  id: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientDOB?: string;
  status: 'pending' | 'approved' | 'rejected' | 'in_progress';
  createdAt: string;
  processedAt?: string;
  processedBy?: string;
  attachments: Attachment[];
  attachmentCount: number;
  hasPhotos: boolean;
  aiProcessed?: boolean;
  aiConfidence?: number;
  aiNotes?: string;
  rejectionReason?: string;
  rejectionEmailSent?: boolean;
  acceptanceEmailSent?: boolean;
  trackingId?: string;
  emailType?: string;
  from?: string;
  fromName?: string;
  subject?: string;
  bodyPreview?: string;
  s3Location?: string;
  dueDate?: string;
  associatedClaimCount?: number;
}

export interface Attachment {
  filename: string;
  contentType: string;
  size?: number;
  url?: string;
  thumbnailUrl?: string;
  secureUrl?: string;
  previewUrl?: string;
}

export interface IDVerificationListResponse {
  tasks: IDVerificationTask[];
  total: number;
  skip: number;
  limit: number;
}

export interface IDVerificationFilters {
  status?: string;
  search?: string;
  skip?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// API client for ID verification endpoints
class IDVerificationAPI {
  private baseURL = `${API_URL}/api/id-verification`;

  // Get authorization header
  private getAuthHeader() {
    const token = localStorage.getItem('token');
    return token ? { 
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    } : {};
  }

  // List ID verification tasks with filters
  async listTasks(filters: IDVerificationFilters = {}): Promise<IDVerificationListResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.skip !== undefined) params.append('skip', filters.skip.toString());
      if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      
      const response = await axios.get<IDVerificationListResponse>(`${this.baseURL}/tasks?${params.toString()}`, {
        headers: this.getAuthHeader()
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching ID verification tasks:', error);
      throw error;
    }
  }

  // Get single ID verification task
  async getTask(taskId: string): Promise<IDVerificationTask> {
    try {
      const response = await axios.get<IDVerificationTask>(`${this.baseURL}/tasks/${taskId}`, {
        headers: this.getAuthHeader()
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching ID verification task ${taskId}:`, error);
      throw error;
    }
  }

  // Update ID verification task
  async updateTask(taskId: string, updates: Partial<IDVerificationTask>): Promise<IDVerificationTask> {
    try {
      const response = await axios.put<IDVerificationTask>(
        `${this.baseURL}/tasks/${taskId}`,
        updates,
        { headers: this.getAuthHeader() }
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error updating ID verification task ${taskId}:`, error);
      throw error;
    }
  }

  // Approve ID verification
  async approveTask(taskId: string, sendEmail: boolean = true): Promise<any> {
    try {
      const response = await axios.post(
        `${this.baseURL}/tasks/${taskId}/approve`,
        { sendEmail },
        { headers: this.getAuthHeader() }
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error approving ID verification task ${taskId}:`, error);
      throw error;
    }
  }

  // Reject ID verification with reason
  async rejectTask(taskId: string, reason: string, sendEmail: boolean = true): Promise<any> {
    try {
      const response = await axios.post(
        `${this.baseURL}/tasks/${taskId}/reject`,
        { reason, sendEmail },
        { headers: this.getAuthHeader() }
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error rejecting ID verification task ${taskId}:`, error);
      throw error;
    }
  }

  // Mark as duplicate
  async markAsDuplicate(taskId: string): Promise<any> {
    try {
      const response = await axios.post(
        `${this.baseURL}/tasks/${taskId}/duplicate`,
        {},
        { headers: this.getAuthHeader() }
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error marking task ${taskId} as duplicate:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const idVerificationAPI = new IDVerificationAPI();
